/**
 * Catastrophic Failure Horizontal Bar Chart Component - Override Component
 * Specific implementation for catastrophic-failure screen
 * Inherits same sorting logic from parent
 */
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { formatStat, formatDecimalWithCommas } from '@shared-lib/utils/format.utils';
import { HorizontalBarChartComponent as ParentHorizontalBarChartComponent } from '@shared-lib/chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { DataErrorComponent } from '@shared-lib/global-components/data-error/data-error.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';

@Component({
  selector: 'app-catastrophic-failure-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, DataErrorComponent],
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrls: ['./horizontal-bar-chart.component.scss']
})
export class CatastrophicFailureHorizontalBarChartComponent extends ParentHorizontalBarChartComponent {
  // Constructor is inherited from parent to avoid dependency injection issues
  displayData: { category: string, value: number, percentage: string, showBox: boolean }[] = [];
  boxYPositions: Record<string, number> = {};
  hasPixelBoxPositions = false;
  noteBoxStyle: any = {};
  private responsiveBindingsInitialized = false;
  private router = inject(Router);

  private getSelectedEngineKeyFromNav(): string {
    const navState = (window?.history?.state || {}) as any;
    return `${navState?.selectedEngineModelKey ?? navState?.engine_model_key ?? navState?.engine_model ?? navState?.selectedEngine ?? ''}`.trim();
  }

  private formatFleetEngineModel(value: string): string {
    const cleaned = `${value || ''}`
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^%+|%+$/g, '');

    return cleaned ? `%${cleaned}%` : '';
  }

  private normalizeEngineKey(value: string): string {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, '');
  }

  private isFleetSummaryKey(key: string): boolean {
    const normalized = this.normalizeEngineKey(key);
    // Handle both underscore and space versions
    return normalized === 'totalnoengines' ||
      normalized === 'total_no_engines' ||
      normalized === 'new' ||
      normalized === 'newengines' ||
      normalized === 'new_engines' ||
      normalized === 'rebuild' ||
      normalized === 'rebuilt' ||
      normalized === 'rebuiltengines' ||
      normalized === 'rebuilt_engines';
  }

  private getFleetLifeStageOrder(category: string): number {
    const normalized = this.normalizeEngineKey(category);
    
    // Define the expected order for Fleet Life Stage categories (engine run hours)
    // Support multiple range formats from backend
    const orderMap: Record<string, number> = {
      '<3k': 1,
      '3k-6k': 2,
      '3k-8k': 2,
      '6k-11k': 3,
      '8k-11k': 3,
      '11k-15k': 4,
      '11k-16k': 4,
      '15k-20k': 5,
      '>20k': 6
    };
    
    const order = orderMap[normalized] !== undefined ? orderMap[normalized] : 999;
    console.log('[CF HBC] getFleetLifeStageOrder:', { category, normalized, order });
    return order;
  }

  private getYearOrder(category: string): number {
    // For FailureDetails - sort by year (numeric)
    const year = parseInt(category, 10);
    if (!isNaN(year)) {
      console.log('[CF HBC] getYearOrder:', { category, year });
      return year;
    }
    return 9999; // Unknown/non-year categories go to end
  }

  private sortFleetLifeStageCategories(categories: string[], values: number[]): { categories: string[], values: number[] } {
    // Create array of tuples to keep categories and values in sync
    const pairs = categories.map((cat, idx) => ({ category: cat, value: values[idx] }));
    
    // Detect if we're sorting years or Fleet Life Stage
    const isYear = categories.some(cat => /^\d{4}$/.test(cat));
    
    // Sort by appropriate logic
    pairs.sort((a, b) => {
      if (isYear) {
        // Sort by year (numeric)
        const orderA = this.getYearOrder(a.category);
        const orderB = this.getYearOrder(b.category);
        return orderA - orderB;
      } else {
        // Sort by Fleet Life Stage order
        const orderA = this.getFleetLifeStageOrder(a.category);
        const orderB = this.getFleetLifeStageOrder(b.category);
        return orderA - orderB;
      }
    });
    
    // Unzip back into separate arrays
    return {
      categories: pairs.map(p => p.category),
      values: pairs.map(p => p.value)
    };
  }

  private resolveFleetLifeRecord(res: any): any {
    // Unwrap nested responseList from backend
    if (res && !Array.isArray(res) && Array.isArray(res.responseList)) {
      let current = res;
      while (current && Array.isArray(current.responseList) && current.responseList.length > 0) {
        current = current.responseList[0];
      }
      // After unwrapping, check if we have a data array or a single object
      if (current && Array.isArray(current.data)) {
        res = current.data;
      } else if (current && (current.groupData || current.engine_name)) {
        // For FailureDetails responses: { engine_name, groupData }
        res = current;
      } else {
        res = current;
      }
    }

    if (!Array.isArray(res)) {
      return res;
    }

    const selectedEngine = this.normalizeEngineKey(this.getSelectedEngineKeyFromNav());
    if (!selectedEngine) {
      return res[0];
    }

    const exactMatch = res.find((item: any) => this.normalizeEngineKey(item?.engine_name) === selectedEngine);
    if (exactMatch) {
      return exactMatch;
    }

    const baseEngine = selectedEngine.split(',')[0].trim();
    const prefixMatch = res.find((item: any) => this.normalizeEngineKey(item?.engine_name).startsWith(`${baseEngine},`));
    return prefixMatch || res[0];
  }

  private get isFailureDetailsChart(): boolean {
    const customData = this.data;
    const ep = customData?.customPayload?.endpoints || customData?.endpoints || customData;
    if (Array.isArray(ep)) {
      return ep.some((e: any) => e.functionName === 'FailureDetails');
    } else if (ep?.functionName === 'FailureDetails') {
      return true;
    }
    return false;
  }

  constructor(
    configService: ConfigService,
    globalFilterService: GlobalFilterService,
    chartFilterHandler: ChartFilterHandlerService
  ) {
    super(configService, globalFilterService, chartFilterHandler);
  }

  override ngAfterViewInit(): void {
    console.log('[CF HBC] ngAfterViewInit - skipping super to avoid parent filter$ conflict');
    // DO NOT call super.ngAfterViewInit() - it sets up a filter$ subscription
    // that processes the response with old logic and clears the chart

    // Trigger initial load since it was removed from parent
    // But only if we don't have a chart yet or it's not loading
    setTimeout(() => {
      const chartDom = document.getElementById(this.data?.widgetId);
      if (chartDom && !this.myChart) {
        console.log('[CF HBC] Triggering necessary initial load');
        this.loadAndRenderChart().pipe(takeUntil(this.unSubscribe$)).subscribe({
          next: (res: any) => {
            console.log('[CF HBC] Initial load success', res);

            // Read config keys from repower-screen-2.json
            const dataKey = this.data.dataKey || 'groupData';
            const valueKey = this.data.valueKey || 'count';
            const percentageKey = this.data.percentageKey || 'percentage';

            const resolvedRecord = this.resolveFleetLifeRecord(res);

            // For FleetLifeStageService, response may be an array of engine records.
            // Resolve the record matching current navigation engine.
            const groupData = resolvedRecord?.[dataKey];
            // Try to get total from groupData first, else fallback to res property
            let totalNoEngines = resolvedRecord?.total_no_engines || 0;
            if (groupData && groupData['total_no_engines'] && groupData['total_no_engines'][valueKey]) {
              totalNoEngines = groupData['total_no_engines'][valueKey];
            }
            // Also check for "Total No Engines" key (with space)
            if (groupData && groupData['Total No Engines'] && groupData['Total No Engines'][valueKey]) {
              totalNoEngines = groupData['Total No Engines'][valueKey];
            }
            console.log('[CF HBC] Extracted totalNoEngines:', totalNoEngines);

            if (groupData) {
              console.log('[CF HBC] ========== CATASTROPHIC FAILURE FLEET LIFE STAGE SORT DEBUG START ==========');
              console.log('[CF HBC] Found groupData:', groupData);
              const categories: string[] = [];
              const values: number[] = [];

              // Build flat arrays for chart (all items) & displayData for boxes (only with percentage)
              const allItems: { key: string; count: number; percentage?: number }[] = [];
              this.displayData = [];

              let newCount = 0;
              let rebuildCount = 0;

              Object.keys(groupData).forEach(key => {
                // Summary keys are represented inside the total stacked bar, not as separate rows.
                if (this.isFleetSummaryKey(key)) {
                  const normalizedKey = this.normalizeEngineKey(key);
                  if (normalizedKey === 'new' || normalizedKey === 'new_engines') {
                    newCount = groupData[key]?.[valueKey] || 0;
                  }
                  if (
                    normalizedKey === 'rebuild' ||
                    normalizedKey === 'rebuilt' ||
                    normalizedKey === 'rebuilt_engines'
                  ) {
                    rebuildCount = groupData[key]?.[valueKey] || 0;
                  }
                  return;
                }

                const item = groupData[key];
                const count = item[valueKey] || 0;
                categories.push(key);
                values.push(count);

                // Use backend percentage directly if available
                const backendPercentage = item?.[percentageKey];
                const hasPercentage = backendPercentage !== undefined && backendPercentage >= 0;
                this.displayData.push({
                  category: key,
                  value: count,
                  percentage: hasPercentage ? `${backendPercentage}%` : '',
                  showBox: hasPercentage
                });
              });

              // Sort Fleet Life Stage categories - always attempt to sort mapped categories
              console.log('[CF HBC] Before sorting - categories:', categories);
              const sorted = this.sortFleetLifeStageCategories(categories, values);
              console.log('[CF HBC] After sorting - categories:', sorted.categories);
              
              // Detect if we're dealing with years
              const isYear = categories.some(cat => /^\d{4}$/.test(cat));
              
              // Reverse for horizontal bar chart (ECharts renders bottom-to-top)
              categories.length = 0;
              categories.push(...sorted.categories.reverse());
              values.length = 0;
              values.push(...sorted.values.reverse());
              console.log('[CF HBC] After reverse - categories:', categories);

              // Re-sort displayData to match sorted categories
              this.displayData.sort((a, b) => {
                if (isYear) {
                  const orderA = this.getYearOrder(a.category);
                  const orderB = this.getYearOrder(b.category);
                  return orderA - orderB;
                } else {
                  const orderA = this.getFleetLifeStageOrder(a.category);
                  const orderB = this.getFleetLifeStageOrder(b.category);
                  return orderA - orderB;
                }
              });
              console.log('[CF HBC] Sorted displayData:', this.displayData);

              const isFailure = this.isFailureDetailsChart;

              // For both Fleet Life Stage and FailureDetails, add Total No Engines
              if (totalNoEngines > 0) {
                // Insert Total No Engines at END (renders at top due to reversal)
                categories.push('Total No Engines');
                values.push(totalNoEngines);
                // Add to displayData - use backend percentage if available, else calculate
                const totalEngineBackendPercentage = groupData?.['total_no_engines']?.[percentageKey] || groupData?.['Total No Engines']?.[percentageKey];
                const totalEnginehasPercentage = totalEngineBackendPercentage !== undefined && totalEngineBackendPercentage >= 0;
                this.displayData.push({
                  category: 'Total No Engines',
                  value: totalNoEngines,
                  percentage: totalEnginehasPercentage ? `${totalEngineBackendPercentage}%` : '',
                  showBox: totalEnginehasPercentage
                });
                console.log('[CF HBC] Added Total No Engines to displayData');

                // Store division for Total No Engines using explicit backend counts (for Fleet Life Stage only)
                if (!isFailure) {
                  const resolvedNewCount = newCount > 0 || totalNoEngines === 0
                    ? newCount
                    : Math.max(totalNoEngines - rebuildCount, 0);
                  (this as any).totalEngineDivision = {
                    new: resolvedNewCount,
                    rebuild: rebuildCount,
                    total: totalNoEngines
                  };
                }
              }

              console.log('[CF HBC] categories:', categories);
              console.log('[CF HBC] values:', values);
              console.log('[CF HBC] displayData:', this.displayData);

              if (categories.length <= 1) { // 1 because 'Total No Engines' is always added
                this.hasError = true;
                this.errorMessage = 'No data currently available';
              } else {
                this.hasError = false;
                // Pass flat structure to renderChart (it expects { category: [], value: [] })
                this.renderChart({ category: categories, value: values });
                this.initializeResponsiveBindings();
              }
            } else {
              this.hasError = true;
              this.errorMessage = 'No data available';
            }
          }
        });
      }
    }, 100);
  }

  protected override loadAndRenderChart(filters?: any): any {
    console.log('[CF HBC] loadAndRenderChart', filters);
    this.currentFilters = filters;
    const chartDom = document.getElementById(this.data?.widgetId);
    console.log('[CF HBC] loadAndRenderChart - chartDom:', chartDom ? 'found' : 'NOT FOUND', this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      console.log('[CF HBC] Initializing ECharts');
      const height = this.data.chartConfig?.height || this.data.height || '400px';
      chartDom.style.height = height;
      this.myChart = echarts.init(chartDom);
    } else {
      console.log('[CF HBC] ECharts already initialized');
    }

    this.myChart.showLoading();

    // Get dynamic payload data from router state
    const navState = window.history.state || {};

    console.log('[CF HBC] Router state from history:', navState);

    let mineSite = navState?.mine_site || navState?.selectedMineName || '';
    let engineModel = navState?.engine_model || navState?.selectedEngineModelKey || navState?.selectedEngine || '';

    console.log('[CF HBC] Extracted - mineSite:', mineSite, 'engineModel:', engineModel);

    engineModel = this.formatFleetEngineModel(engineModel);
    console.log('[CF HBC] Formatted engineModel:', engineModel);

    // Build dynamic payload if we have the data
    let customData = JSON.parse(JSON.stringify(this.data || {}));

    let isFailureDetails = false;
    const ep = customData?.customPayload?.endpoints || customData?.endpoints || customData;
    if (Array.isArray(ep)) {
      isFailureDetails = ep.some((e: any) => e.functionName === 'FailureDetails');
    } else if (ep?.functionName === 'FailureDetails') {
      isFailureDetails = true;
    }

    if (mineSite || engineModel) {
      console.log('[CF HBC] Found dynamic data - mineSite:', mineSite, 'engineModel:', engineModel);

      if (!customData.customPayload) {
        customData.customPayload = {};
      }
      if (!customData.customPayload.requestData) {
        customData.customPayload.requestData = {};
      }

      // Update the customPayload with dynamic values
      if (mineSite) {
        customData.customPayload.requestData.mine_site = mineSite;
      }
      if (engineModel && !isFailureDetails) {
        customData.customPayload.requestData.engine_model = engineModel;
      }

      if (isFailureDetails && customData.customPayload.requestData) {
        delete customData.customPayload.requestData.engine_model;
        delete customData.customPayload.requestData.engine_model_key;
        delete customData.customPayload.requestData.mine;
        delete customData.customPayload.requestData.region;
      }

      console.log('[CF HBC] Updated customPayload:', customData.customPayload);
    } else {
      console.log('[CF HBC] No dynamic data found, using default payload from config');
    }

    const cleanFilters = { ...filters, useQueryParams: this.data?.useQueryParams || false } as any;
    if (isFailureDetails) {
      delete cleanFilters.engine_model;
      delete cleanFilters.engine_model_key;
      delete cleanFilters.mine;
      delete cleanFilters.region;
    }

    return this.configService.getConfig(
      this.data.apiUrl,
      customData,  // Use updated data with dynamic payload
      cleanFilters,
      this.data.method
    ).pipe(takeUntil(this.unSubscribe$));
  }

  protected override renderChart(chartData?: any): any {
    console.log('[CF HBC] renderChart', chartData);
    if (!this.data || !chartData || !chartData.category || chartData.category.length === 0) {
      console.warn('[CF HBC] Missing data or config', { data: this.data, chartData });
      this.hasError = true;
      this.errorMessage = 'No data currently available';
      return;
    }

    // Reset error state if data is present
    this.hasError = false;

    // displayData is already populated in ngAfterViewInit subscriber with real percentages
    // Only recalculate if it wasn't set (fallback for flat data)
    if (this.displayData.length === 0) {
      const total = (chartData.value || []).reduce((acc: number, val: number) => acc + val, 0);
      this.displayData = (chartData.category || []).map((cat: string, index: number) => {
        const val = (chartData.value || [])[index];
        const percent = total > 0 ? ((val / total) * 100).toFixed(1) : '0';
        return { category: cat, value: val, percentage: `${percent}%`, showBox: true };
      });
    }
    console.log('[CF HBC] displayData', this.displayData);

    const extendLeft = this.data.orientation === 'left';

    // Set dynamic height if provided
    const chartDom = document.getElementById(this.data.widgetId);
    console.log('[CF HBC] renderChart - chartDom:', chartDom ? 'found' : 'NOT FOUND', {
      width: chartDom?.clientWidth,
      height: chartDom?.clientHeight
    });
    if (chartDom) {
      const height = this.data.chartConfig?.height || this.data.height || '400px';
      chartDom.style.height = height;
    }
    const containerWidth = chartDom?.clientWidth || 0;
    const compactLayout = containerWidth > 0 && containerWidth < 1200;
    const axisNameFontSize = this.data?.chartConfig?.axisNameFontSize ?? (compactLayout ? 14 : 18);
    const yAxisLabelFontSize = this.data?.chartConfig?.yAxisLabelFontSize ?? (compactLayout ? 13 : 16);
    const valueLabelFontSize = this.data?.chartConfig?.valueLabelFontSize ?? (compactLayout ? 14 : 18);
    const yAxisNameGap = this.data?.chartConfig?.nameGap ?? (compactLayout ? 145 : 170);
    const gridLeft = this.data?.chartConfig?.gridLeft ?? (compactLayout ? 180 : 220);
    const gridRight = this.data?.chartConfig?.gridRight ?? 40;
    const gridTop = this.data?.chartConfig?.gridTop ?? '10%';
    const gridBottom = this.data?.chartConfig?.gridBottom ?? '12%';
    const barWidth = this.data?.chartConfig?.barWidth;

    const option: any = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          if (!Array.isArray(params)) params = [params];
          const firstParam = params[0];
          const category = firstParam?.name || '';

          if (category === 'Total No Engines' && (this as any).totalEngineDivision) {
            const div = (this as any).totalEngineDivision;
            return `<strong>Total No Engines (${div.total})</strong><br/>New: ${div.new}<br/>Rebuilt: ${div.rebuild}`;
          }

          const value = firstParam?.value || 0;
          const tooltipLabels = this.data?.tooltipLabels || {};
          const categoryLabel = tooltipLabels.category || 'Category';
          const valueLabel = tooltipLabels.value || 'Value';
          const formattedValue = this.data?.useCommaStat
            ? formatDecimalWithCommas(value)
            : value;
          return `${categoryLabel}: ${category}<br/>${valueLabel}: ${formattedValue}`;
        }
      },
      grid: {

        left: gridLeft, // space for y-axis category labels
        right: gridRight,

        top: gridTop,
        bottom: gridBottom,
        containLabel: false
      },
      xAxis: {
        type: 'value',
        inverse: extendLeft,
        name: this.isFailureDetailsChart ? 'Count of Failure Engines' : (this.data?.chartConfig?.xAxisName || 'Engines'),
        nameLocation: 'middle',
        nameGap: this.isFailureDetailsChart ? 5 : 35,
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: axisNameFontSize,
          color: '#1a1a1a'
        },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { show: false },
        splitLine: { show: false }
      },
      yAxis: {
        type: 'category',
        inverse: false,
        name: this.isFailureDetailsChart ? 'Year' : (this.data?.chartConfig?.yAxisName || 'Engine Run Hours'),
        nameLocation: 'middle',
        nameGap: yAxisNameGap,
        nameRotate: 90, // Rotates it vertically
        nameTextStyle: {
          fontWeight: 'bold',
          fontSize: axisNameFontSize,
          color: '#1a1a1a'
        },
        data: chartData?.category || [],
        axisLabel: {
          fontWeight: 'bold', // Matching line chart legends
          fontSize: yAxisLabelFontSize,
          color: '#1a1a1a', // Distinct dark color
          fontFamily: 'sans-serif',
          formatter: (value: string) => {
            const label = value === 'total_no_engines' ? 'Total No Engines' : value;
            return label.length > 30 ? label.slice(0, 30) + '…' : label;
          }
        }
      },
      series: this.buildChartSeries(chartData, valueLabelFontSize)
    };

    if (this.myChart) {
      this.myChart.setOption(option, true); // true to clear previous configuration
      this.syncPercentageBoxPositions();
      this.myChart.off('mouseover');
      this.myChart.off('mouseout');

      setTimeout(() => {
        this.myChart.resize();
        this.syncPercentageBoxPositions();
        this.myChart.hideLoading();
      }, 500);
    }
  }

  private buildChartSeries(chartData: any, valueLabelFontSize: number): any[] {
    const isFailure = this.isFailureDetailsChart;
    const barWidth = this.data?.chartConfig?.barWidth;

    const mainSeries: any = {
      type: 'bar',
      stack: 'engines',
      ...(barWidth !== undefined ? { barWidth } : {}),
      label: { show: true },
      data: (chartData?.category || []).map((cat: any, index: any) => {
        const value = chartData?.value[index];
        const isSelected = this.selectedCategory === null || this.selectedCategory === cat;
        const isTotalEngines = cat === 'Total No Engines';
        const is2025 = cat === '2025';

        // For FailureDetails, show Total No Engines bar with actual value
        // For Fleet Life Stage, show it as transparent (will be shown via stacked series)
        if (isTotalEngines && !isFailure) {
          return {
            value: 0,
            itemStyle: { color: 'transparent' },
            label: { show: false }
          };
        }

        return {
          value,
          itemStyle: {
            color: isSelected
              ? '#b6d79b'
              : 'rgba(200, 200, 200, 0.3)',
            borderColor: is2025 && isFailure ? '#d46a6a' : 'transparent',
            borderWidth: is2025 && isFailure ? 3 : 0
          },
          label: {
            show: true,
            position: 'right',
            distance: 10,
            color: 'black',
            fontWeight: 'bold',
            fontSize: valueLabelFontSize,
            formatter: ({ value }: any) => formatStat(value)
          }
        };
      }) || []
    };

    const series: any[] = [mainSeries];

    // Only add New/Rebuilt series for Fleet Life Stage (not for FailureDetails)
    if (!isFailure) {
      series.push({
        name: 'New',
        type: 'bar',
        stack: 'engines',
        ...(barWidth !== undefined ? { barWidth } : {}),
        data: (chartData?.category || []).map((cat: any) => {
          if (cat === 'Total No Engines' && (this as any).totalEngineDivision) {
            return (this as any).totalEngineDivision.new;
          }
          return 0;
        }),
        itemStyle: { color: this.data.barColor || '#91CC75' },
        label: { show: false }
      });

      series.push({
        name: 'Rebuilt',
        type: 'bar',
        stack: 'engines',
        ...(barWidth !== undefined ? { barWidth } : {}),
        data: (chartData?.category || []).map((cat: any) => {
          if (cat === 'Total No Engines' && (this as any).totalEngineDivision) {
            return (this as any).totalEngineDivision.rebuild;
          }
          return 0;
        }),
        itemStyle: { color: this.data.barColor || '#91CC75' },
        label: {
          show: true,
          position: 'right',
          distance: 10,
          color: 'black',
          fontWeight: 'bold',
          fontSize: valueLabelFontSize,
          formatter: (params: any) => {
            if (params.name === 'Total No Engines' && (this as any).totalEngineDivision) {
              return formatStat((this as any).totalEngineDivision.total);
            }
            return '';
          }
        }
      });
    }

    return series;
  }

  getPercentageBoxTop(category: string): number | null {
    const top = this.boxYPositions[category];
    return Number.isFinite(top) ? top : null;
  }

  private syncPercentageBoxPositions(): void {
    if (!this.myChart || !this.displayData?.length) {
      this.boxYPositions = {};
      this.hasPixelBoxPositions = false;
      return;
    }

    const nextPositions: Record<string, number> = {};
    for (const item of this.displayData) {
      const y = this.myChart.convertToPixel({ yAxisIndex: 0 }, item.category);
      if (typeof y === 'number' && Number.isFinite(y)) {
        nextPositions[item.category] = y;
      }
    }

    this.boxYPositions = nextPositions;
    this.hasPixelBoxPositions = Object.keys(nextPositions).length > 0;
    
    // Initialize note box style for display
    if (this.isFailureDetailsChart && this.data.showStack) {
      this.noteBoxStyle = { visible: true };
    } else {
      this.noteBoxStyle = {};
    }
  }

  private initializeResponsiveBindings(): void {
    if (this.responsiveBindingsInitialized) {
      return;
    }

    this.addResizeListener();
    this.reflowSub = this.responsiveLayoutService.reflow$.pipe(takeUntil(this.unSubscribe$)).subscribe(() => {
      this.myChart?.resize();
      this.syncPercentageBoxPositions();
    });
    this.responsiveBindingsInitialized = true;
  }

  protected override setupChartClickEvent(): any {
    // TODO: Add your custom logic here
    return super.setupChartClickEvent();
  }

  protected override handleResize = (): void => {
    this.myChart?.resize();
    this.syncPercentageBoxPositions();
  };

  protected override addResizeListener(): any {
    if (this.responsiveBindingsInitialized) {
      return;
    }
    return super.addResizeListener();
  }
}
